import { logEvent } from "@firebase/analytics";
import {
    FacebookAuthProvider,
    getAuth, GoogleAuthProvider, signInWithPopup,
    signOut as logout,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    User,
    UserCredential
} from "firebase/auth";
import { collection, doc, getDoc, getFirestore, setDoc } from "firebase/firestore";
import { createContext, FC, ReactNode, useContext, useEffect, useState } from "react";
import { LoadingComponent } from "../components/LoadingComponent";
import { UserData } from "../models/UserData";
import { updateUserDataDocument } from "../services/events/UserDataEventService";
import { analytics, firebaseApp } from "./firebase";
import { FirebaseConverter } from "./firebaseConverter";


type AuthContextType = {
    user: User | null,
    userData?: UserData
    signInWithGoogle: () => void,
    signInWithFacebook: () => void,
    signOut: () => void,
    updateUserData: (userData: UserData) => Promise<void>
    signIn(email: string, password: string): Promise<UserCredential>,
    register(email: string, password: string): Promise<UserCredential>,
    resetPassword(email: string): Promise<void>
}
const AuthContext = createContext<AuthContextType>({ user: null, signInWithGoogle: () => { }, signInWithFacebook: () => { }, signOut: () => { }, updateUserData: () => Promise.reject(), signIn: () => Promise.reject(), register: () => Promise.reject(), resetPassword: () => Promise.reject() });


export const useAuth = () => {
    return useContext(AuthContext);
};
type AuthProviderProps = {
    children: ReactNode
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [userData, setUserData] = useState<UserData>();
    const [isLoading, setLoading] = useState(true);
    const googleProvider = new GoogleAuthProvider();
    const facebookProvider = new FacebookAuthProvider();
    const db = getFirestore(firebaseApp);
    const users = collection(db, "users")

    useEffect(() => {
        const unsubscribe = getAuth().onAuthStateChanged(user => {
            console.log("onAuthStateChanged", user)
            setLoading(false)
            setUser(user);
        });
        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (user) {
            console.log("creating user data for user", user)
            getOrCreateuserData()
        } else {
            console.log("user is null")
            setUserData(undefined)
        }
        // eslint-disable-next-line
    }, [user])

    useEffect(() => {
        if (isLoading) {
            logEvent(analytics, "login")
        }
    }, [isLoading])

    const getPlAuth = () => {
        const auth = getAuth(firebaseApp);
        auth.languageCode = 'pl';
        return auth;
    }

    const signInWithGoogle = async () => {
        signInWithPopup(getPlAuth(), googleProvider);
    };

    const signInWithFacebook = async () => {
        signInWithPopup(getPlAuth(), facebookProvider);
    };

    const signIn = async (email: string, password: string): Promise<UserCredential> => signInWithEmailAndPassword(getPlAuth(), email, password).catch((error) => Promise.reject(error));
    const register = async (email: string, password: string): Promise<UserCredential> => createUserWithEmailAndPassword(getPlAuth(), email, password).catch((error) => Promise.reject(error));
    const resetPassword = async (email: string) => sendPasswordResetEmail(getPlAuth(), email).catch((error) => Promise.reject(error));

    const updateUserData = async (userData: UserData): Promise<void> => {
        if (user) {
            return updateUserDataDocument(userData).then(() => setUserData(userData))
        } else {
            return Promise.reject("there is no current user in session")
        }
    }
    async function getOrCreateuserData() {
        try {
            if (user) {
                const docRef = doc(users, user.uid).withConverter(new FirebaseConverter<UserData>())
                var userData = (await getDoc(docRef)).data();
                if (!userData) {
                    userData = new UserData(user.uid, user.displayName || undefined, user.email || undefined)
                    await setDoc(docRef, userData)
                }
                console.log("userData", userData)
                setUserData(userData)

            }
        }
        catch (err: any) {
            console.error(err);
        }
    }
    const signOut = () => {
        logout(getAuth(firebaseApp));
    };
    return (
        <AuthContext.Provider value={{ user, userData, signInWithGoogle, signInWithFacebook, signOut, updateUserData, signIn, register, resetPassword }}>
            {!isLoading && children}
            {isLoading && <LoadingComponent message="Ładowanie" />}
        </AuthContext.Provider>
    );
}
